import {
  defaultTemplateIdentifier,
  formatDate,
  removeTemplateTags,
} from "@certn/utils";
import { ArrayControlProps, ControlProps, JsonSchema } from "@jsonforms/core";
import i18n from "i18next";
import range from "lodash/range";
import { ComponentType, useContext, useEffect } from "react";

import { BaseLayoutContext } from "../layouts/BaseLayoutContext";
import { getPath } from "../utils";
import { LayoutProps } from "@jsonforms/core";

export type RequirementDisplayStyle = "success" | "error" | "normal";
export function getRequirementsList(
  requirementMessages: string[],
  errors: string[],
  isSubmitted: boolean | undefined,
  label: string,
  alwaysDisplayAsBulletPoints = false
) {
  function getStyle(requirement: string): RequirementDisplayStyle {
    // Pre-submit, AJV/required validation has not yet run, so an empty `errors`
    // array does NOT mean the requirement is satisfied — it just means the form
    // hasn't been validated. Reserve the green "success" checkmark for after
    // submit (FP-963: Address History showed every requirement as satisfied
    // on page load even with no address entered).
    const found = errors.some((error) => error.includes(requirement));
    if (found) {
      return isSubmitted ? "error" : "normal";
    }
    return isSubmitted ? "success" : "normal";
  }

  return requirementMessages.map((requirement) => ({
    text: `${label} ${requirement}`,
    style: (alwaysDisplayAsBulletPoints
      ? "normal"
      : getStyle(requirement)) satisfies RequirementDisplayStyle,
  }));
}

// TODO replace this function with something a bit more dynamic
// https://certn.atlassian.net/browse/CON-596
export function replaceTemplateWithValue(
  value: string,
  item: Record<string, unknown>,
  regexIdentifier = defaultTemplateIdentifier
) {
  const identifier = new RegExp(`${regexIdentifier}`, "g");
  const regexMatches = value?.match(identifier);

  if (!regexMatches) {
    if (value?.split(" ").length === 1) {
      return getPath(item, value);
    } else {
      /**
       * if something like "hello {world}" is passed in
       * and there isn't any valid item or regex match then
       * just return the input value
       */
      return value;
    }
  }

  const matchedWords = value.match(identifier);
  const updatedMatchedWords = matchedWords?.map((val) => {
    const updatedText = getPath(item, removeTemplateTags(val, regexIdentifier));
    return updatedText;
  });
  if (updatedMatchedWords?.some((word) => !word)) {
    // If we don't get a value back for a given template then return undefined
    // prevents things like "null years old" displaying as in this case "age" is an optional field
    return undefined;
  }

  let modifiedString = value;
  matchedWords?.forEach((val, index) => {
    if (updatedMatchedWords && index < updatedMatchedWords?.length) {
      modifiedString = modifiedString.replace(val, updatedMatchedWords[index]);
    }
  });
  return modifiedString;
}

// TODO replace this function
// https://certn.atlassian.net/browse/CON-596
export function getItemList(
  labels: string[],
  data: Record<string, unknown>,
  regexIdentifier = defaultTemplateIdentifier
): string[] {
  const itemList = labels.map((item) => {
    return replaceTemplateWithValue(item, data, regexIdentifier);
  });
  return itemList;
}

export function getDecimalPoints(multipleOf: number) {
  const str = multipleOf.toString();
  if (str.includes(".")) {
    return str.split(".")[1].length;
  } else {
    return 0;
  }
}
export function checkIfDivisible(num: number, by: number): boolean {
  const TOLERANCE = 1e-10;

  const remainder = Math.abs(num) % by;
  if (remainder < TOLERANCE) return true;
  return Math.abs(by) - remainder < TOLERANCE;
}

export function splitAndCapitalize(string: string) {
  if (typeof string !== "string") {
    return string;
  }

  if (string.includes("_")) {
    return string
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  return string;
}

export function withArrayControlVisibility(
  Component: ComponentType<ArrayControlProps>
) {
  return function Render(props: ArrayControlProps) {
    const parent: LayoutProps = useContext(BaseLayoutContext);

    useEffect(() => {
      // If this array control is hidden or a parent layout is hidden, remove all items
      if (parent.visible === false || props.visible === false) {
        props.removeItems?.(props.path, range(0, props.data?.length))();
      }
    }, [parent.visible, props.visible]);

    if (parent.visible === false || props.visible === false) {
      return null;
    }

    return (
      <Component
        {...props}
        required={Boolean(
          props.required || props.uischema.options?.is_required
        )}
      />
    );
  };
}

export function withControlVisibility(Component: ComponentType<ControlProps>) {
  return function Render(props: ControlProps) {
    const parent: LayoutProps = useContext(BaseLayoutContext);

    useEffect(() => {
      if (parent.visible === false || props.visible === false) {
        props.handleChange(props.path, props?.schema?.default);
      }
    }, [parent.visible, props.visible]);

    if (parent.visible === false || props.visible === false) {
      return null;
    }

    return (
      <Component
        {...props}
        required={Boolean(
          props.required || props.uischema.options?.is_required
        )}
      />
    );
  };
}

export function convertDataToDisplayValues(
  item: unknown,
  schema: JsonSchema
): Record<string, unknown> {
  if (typeof item !== "object" || item === null) {
    return {};
  }

  return Object.entries(item).reduce((acc, [key, value]) => {
    const property = schema?.properties?.[key];

    switch (property?.type) {
      case "string":
        if (property.oneOf) {
          const friendlyValue = property.oneOf?.find(
            (lookup) => lookup.const === value
          );
          return {
            ...acc,
            [key]: friendlyValue?.title || value,
          };
        }

        if (property.format === "date" && typeof value === "string") {
          const friendlyValue = formatDate(value, "PP");
          return {
            ...acc,
            [key]: friendlyValue,
          };
        }

        break;
      case "number":
        return {
          ...acc,
          [key]: Intl.NumberFormat(i18n.language).format(Number(value)),
        };

      case "object":
        if (typeof value === "object" && value !== null) {
          return {
            ...acc,
            [key]: convertDataToDisplayValues(value, property),
          };
        }
    }

    return {
      ...acc,
      [key]: value,
    };
  }, {});
}

export function getScopeLookupPath(scope: string) {
  return scope
    .split("/") // split scope ex: "#/properties/employment_history/properties/employers"
    .slice(1) // remove # starting character
    .reduce(
      (acc: string, value: string, i: number) =>
        (acc += i === 0 ? value : `.${value}`),
      ""
    );
}
